import React from 'react';
import { Box, Button } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import './PaginationBar.css';

interface PaginationBarProps {
  page: number;
  count: number;
  onChange: (page: number) => void;
  disabled?: boolean;
}

type PageEntry = number | 'ellipsis';


const getVisiblePages = (page: number, count: number): PageEntry[] => {
  const siblingCount = 1;
  const totalVisible = siblingCount * 2 + 3; 

  if (count <= totalVisible + 2) {
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  const start = Math.max(2, page - siblingCount);
  const end = Math.min(count - 1, page + siblingCount);

  const pages: PageEntry[] = [1];
  if (start > 2) pages.push('ellipsis');
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < count - 1) pages.push('ellipsis');
  pages.push(count);

  return pages;
};

export const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  count,
  onChange,
  disabled = false,
}) => {
  const visiblePages = getVisiblePages(page, count);

  return (
    <Box className="pagination-bar">
      <Button
        className="pagination-bar-nav"
        startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
        disabled={disabled || page <= 1}
        onClick={() => onChange(page - 1)}
        aria-label="Previous page"
      >
        <span className="pagination-bar-nav-label">Previous</span>
      </Button>

      <Box className="pagination-bar-numbers">
        {visiblePages.map((entry, index) =>
          entry === 'ellipsis' ? (
            <Box key={`ellipsis-${index}`} className="pagination-bar-ellipsis">
              …
            </Box>
          ) : (
            <Box
              key={entry}
              component="button"
              type="button"
              className={`pagination-bar-page ${entry === page ? 'pagination-bar-page--active' : ''}`}
              disabled={disabled}
              onClick={() => onChange(entry)}
            >
              {entry}
            </Box>
          ),
        )}
      </Box>

      <Button
        className="pagination-bar-nav"
        endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
        disabled={disabled || page >= count}
        onClick={() => onChange(page + 1)}
        aria-label="Next page"
      >
        <span className="pagination-bar-nav-label">Next</span>
      </Button>
    </Box>
  );
};
